import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { vouchersApi, type BaseType } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad } from "../ui/useLoad";
import { fuzzyFilter } from "../lib/fuzzy";
import { fmtAmount } from "../lib/money";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtDate } from "../companies/CompanyPicker";
import { toUrl } from "../shell/routes";

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DAYBOOK_KEY = "books.daybook.range";

// One kind of document, listed under its section (SI10 Invoices, RC10 Payments received, PB10 Bills …)
export const DOC_LISTS: Record<string, { title: string; code: string; crumb: string; one: string; newCode: string }> = {
  sales: { title: "Invoices", code: "SI10", crumb: "Sales", one: "New invoice", newCode: "SI01" },
  receipt: { title: "Payments received", code: "RC10", crumb: "Sales", one: "New payment received", newCode: "RC01" },
  credit_note: { title: "Credit notes", code: "CN10", crumb: "Sales", one: "New credit note", newCode: "CN01" },
  purchase: { title: "Bills", code: "PB10", crumb: "Purchases", one: "New bill", newCode: "PB01" },
  payment: { title: "Payments made", code: "PY10", crumb: "Purchases", one: "New payment made", newCode: "PY01" },
  debit_note: { title: "Debit notes", code: "DN10", crumb: "Purchases", one: "New debit note", newCode: "DN01" },
};
const fyStart = (d: Date) => new Date(d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear(), 3, 1);

// Day Book: every voucher in a period, drafts included. Type to filter; arrows and Enter open; F4–F9 start a new one.
// With a base type it is that document's list instead (this financial year by default).
export function DayBook({ base }: { base?: BaseType } = {}) {
  const doc = base ? DOC_LISTS[base] : undefined;
  const KEY = base ? `books.list.${base}.range` : DAYBOOK_KEY;
  const { company } = useCompany();
  const nav = useNavigate();
  const now = new Date();
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem(KEY) ?? "null") as { from: string; to: string } | null; } catch { return null; } })();
  const [from, setFrom] = useState(saved?.from ?? iso(base ? fyStart(now) : new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(saved?.to ?? iso(now));
  useEffect(() => { try { sessionStorage.setItem(KEY, JSON.stringify({ from, to })); } catch { /* fine */ } }, [from, to]);
  const { data, error, loading } = useLoad(() => vouchersApi.dayBook(company.id, from, to), [company.id, from, to]);
  const [q, setQ] = useState("");
  const [drafts, setDrafts] = useState(true);
  const [sel, setSel] = useState(0);
  const filter = useRef<HTMLInputElement>(null);
  const rows = useMemo(() => {
    const all = (data ?? []).filter((r) => (drafts || r.status !== "draft") && (!base || r.base_type === base));
    return q.trim() ? fuzzyFilter(all, q, (r) => `${r.particulars ?? ""} ${r.type} ${r.number ?? ""} ${r.narration ?? ""} ${r.reference_no ?? ""}`, 1000) : all;
  }, [data, q, drafts, base]);
  useEffect(() => setSel(0), [q, from, to]);
  const open = (id: string) => nav(toUrl(`vouchers/v/${id}`));
  const total = rows.filter((r) => r.status === "posted").reduce((t, r) => t + r.amount, 0);

  return (
    <section className="page">
      {doc ? <PageHead crumb={doc.crumb} title={doc.title} code={doc.code}>
        <Link className="button primary" to={toUrl(`vouchers/new/${base}`)}><Plus size={15} aria-hidden="true" /> {doc.one} <span className="code">{doc.newCode}</span></Link>
      </PageHead> : <PageHead crumb="Accounting" title="Transaction register" code="JR01" />}
      <div className="toolbar">
        <label className="inline-label">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="inline-label">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <input ref={filter} autoFocus placeholder={doc ? "Filter: party, number, reference" : "Filter: party, type, number, narration"} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter transactions"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
            else if (e.key === "Enter" && rows[sel]) { e.preventDefault(); open(rows[sel].id); }
            else if (e.key === "Escape" && q) { e.preventDefault(); e.stopPropagation(); setQ(""); }
          }} />
        <label className="check"><input type="checkbox" checked={drafts} onChange={(e) => setDrafts(e.target.checked)} /> Drafts</label>
      </div>
      <ErrorNote text={error?.message} />
      {loading && !data ? <Loading /> : rows.length === 0 ? (
        <p className="muted">{doc ? `No ${doc.title.toLowerCase()} in this period. ` : "Nothing in this period. "}Type a code to enter one: SI01 sales invoice, PB01 purchase bill, RC01 receipt, PY01 payment, JE01 journal entry, TR01 transfer.</p>
      ) : (
        <table className="grid-table">
          <thead><tr><th>Date</th><th>{doc ? (doc.crumb === "Sales" ? "Customer" : "Vendor") : "Particulars"}</th><th>{doc ? "Status" : "Type"}</th><th>No.</th><th className="num">Amount</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id} className={`${i === sel ? "sel" : ""} ${r.status !== "posted" ? "dim" : ""}`} onClick={() => open(r.id)} onMouseEnter={() => setSel(i)}
                ref={(el) => { if (i === sel && el) el.scrollIntoView({ block: "nearest" }); }}>
              <td>{fmtDate(r.date)}</td>
              <td>{r.particulars}{r.narration && <div className="muted small clip">{r.narration}</div>}</td>
              <td>{doc ? (r.status === "posted" ? "Posted" : "") : r.type}{r.status !== "posted" && <span className={`tag ${r.status}`}>{r.status}</span>}</td>
              <td className="mono">{r.number ?? ""}</td>
              <td className="num">{fmtAmount(r.amount)}</td>
            </tr>))}</tbody>
          <tfoot><tr><td colSpan={4}>Posted total ({rows.filter((r) => r.status === "posted").length})</td><td className="num">{fmtAmount(total)}</td></tr></tfoot>
        </table>
      )}
    </section>
  );
}
